import { createSelector } from "reselect";
import isEmpty from "lodash-es/isEmpty";
import type { RootState } from "@/store/app/storeTypes";
import type { InteractionBlueprintData } from "@/lib/contentParsers/interactionBlueprintSchema";
import type { SparxsListState } from "@/store/sparxsCatalog/listSlice";
import type { SparxsSearchIndexState } from "@/store/sparxsCatalog/searchIndexSlice";
import { selectSparxsBlueprintCachePersistRehydrated } from "@/store/sparxsCatalog/blueprintCacheSelectors";
import { selectSparxsListPersistRehydrated } from "@/store/sparxsCatalog/listSelectors";
import { selectSparxsSearchIndexPersistRehydrated } from "@/store/sparxsCatalog/searchIndexSelectors";

const selectEditorActiveFileName = (state: RootState) =>
  state.editor.activeSparx;

const selectSparxSessionByFileName = (state: RootState) =>
  state.editor.sparxSession.byFileName;

const selectSparxSliceData = (state: RootState) => state.editor.sparx.data;

const selectSparxSliceOriginalData = (state: RootState) =>
  state.editor.sparx.originalData;

export const selectActiveSparxEditorDataBundle = createSelector(
  [
    selectEditorActiveFileName,
    selectSparxSessionByFileName,
    selectSparxSliceData,
    selectSparxSliceOriginalData,
  ],
  (
    activeFileName,
    byFileName,
    sparxData,
    sparxOriginalData,
  ): {
    activeFileName: string;
    workingData: InteractionBlueprintData | null;
    originalData: InteractionBlueprintData | null;
  } => {
    if (!isEmpty(activeFileName)) {
      const entry = byFileName[activeFileName];
      if (entry != null) {
        return {
          activeFileName,
          workingData: entry.current,
          originalData: entry.original,
        };
      }
    }
    return {
      activeFileName,
      workingData: sparxData,
      originalData: sparxOriginalData,
    };
  },
);

const selectSparxsListState = (state: RootState): SparxsListState =>
  state.editor.sparxsList;

const selectSparxsSearchIndexState = (
  state: RootState,
): SparxsSearchIndexState => state.editor.sparxsSearchIndex;

export const selectCatalogAndIndexContext = createSelector(
  [selectSparxsListState, selectSparxsSearchIndexState],
  (sparxsList, searchIndex) => ({
    sparxsList,
    rowsByFileName: searchIndex.rowsByFileName,
    isIndexing: searchIndex.isIndexing,
  }),
);

export type CatalogPersistHydration = {
  searchIndexPersistRehydrated: boolean;
  sparxsListPersistRehydrated: boolean;
  blueprintCachePersistRehydrated: boolean;
};

export const selectCatalogPersistHydration = createSelector(
  [
    selectSparxsSearchIndexPersistRehydrated,
    selectSparxsListPersistRehydrated,
    selectSparxsBlueprintCachePersistRehydrated,
  ],
  (
    searchIndexPersistRehydrated,
    sparxsListPersistRehydrated,
    blueprintCachePersistRehydrated,
  ): CatalogPersistHydration => ({
    searchIndexPersistRehydrated,
    sparxsListPersistRehydrated,
    blueprintCachePersistRehydrated,
  }),
);
